import { useState, useEffect, useMemo, useRef } from "react";
import { activate } from "../../../../utils/keyboard-activate";
import type { ParsedCSV, SourceType } from "../../../../types/extraction";
import type { ProspectResponse } from "../../../../types/api";
import { uploadRosterFile } from "../../../../services/uploads";
import { getAllProspectsForTeam } from "../../../../services/prospects";
import { getSourceType } from "../../../../utils/csv-parser";
import { getSafeErrorMessage } from "../../../../utils/errorHandler";
import { MAX_FILE_SIZE } from "../../../../constants/extraction";
import { ConfirmModal } from "../../../../components/ConfirmModal";
import { Button } from "../../../../components/Button";
import { ExtractionProgress } from "./ExtractionProgress";
import { FieldMappingStep } from "./FieldMappingStep";
import { TeamSelectionStep, type ImportAsNewCandidate } from "./TeamSelectionStep";
import { UploadTypeStep, type UploadType } from "./UploadTypeStep";
import { MultiTeamReviewStep } from "./MultiTeamReviewStep";
import { MergeProspectsModal } from "./merge/MergeProspectsModal";
import {
  describeRosterAddresses,
  summarizeRosterAddresses,
} from "./rosterAddressSummary";
import { ModalCloseButton } from "../../../../components/ModalCloseButton";
import { SentryService } from "../../../../services/sentry";
import "./RosterUploadModal.css";

type UploadStep =
  | "type-selection"
  | "upload"
  | "extracting"
  | "mapping"
  | "team-selection"
  | "multi-team-review"
  | "success";

type SuccessInfo =
  | {
      type: "team";
      teamName: string;
      prospectCount: number;
      teamId: string;
    }
  | {
      type: "multi_team";
      teamCount: number;
      prospectCount: number;
      skippedCount: number;
      duplicateCount: number;
      failedTeams: { teamName: string; error: string }[];
      createdTeamIds: string[];
    };

interface RosterUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (createdTeamIds: string[]) => void;
}

const ALLOWED_FILE_TYPES = [
  "image/jpeg",
  "image/png",
  "application/pdf",
  "text/csv",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-excel",
];

export function RosterUploadModal({
  isOpen,
  onClose,
  onSuccess,
}: RosterUploadModalProps) {
  const [currentStep, setCurrentStep] = useState<UploadStep>("type-selection");
  // The coach's up-front "these are junior college rosters". Pre-arms the per-team toggles at
  // review; it does not replace them, because a showcase batch can hold a prep program alongside
  // the junior colleges and that team has to stay individually correctable.
  const [isJucoUpload, setIsJucoUpload] = useState(false);
  const [uploadType, setUploadType] = useState<UploadType>("single_team");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [sourceType, setSourceType] = useState<SourceType>("image");
  const [gcsUrl, setGcsUrl] = useState<string>("");
  // Originating UploadMetadata id for the uploaded roster image. Threaded into
  // the team-creation commit so the image can be viewed/annotated under the
  // team. Mirrors `gcsUrl` lifecycle exactly (set on upload, cleared on the
  // same resets) — except `handleReExtract`, which re-runs extraction against
  // the same upload and must keep both values.
  const [rosterUploadId, setRosterUploadId] = useState<string>("");
  // The extraction (`roster_extractions.id`) the reviewed rows came from. Sent with
  // every team-creation commit so the import records its provenance. Set only when an
  // extraction completes and cleared on every reset and re-extract, so a commit never
  // carries the id of an earlier or abandoned extraction.
  const [extractionId, setExtractionId] = useState<string>("");
  const [extractedData, setExtractedData] = useState<ParsedCSV | null>(null);
  const [mappedData, setMappedData] = useState<ParsedCSV | null>(null);
  const [successInfo, setSuccessInfo] = useState<SuccessInfo | null>(null);
  const [error, setError] = useState<string>("");
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [hasSuccessfulUpload, setHasSuccessfulUpload] = useState(false);
  const [extractionAttempt, setExtractionAttempt] = useState(0);
  // Accumulates IDs of teams created across one or more uploads within a single
  // modal session (the user can click "Add More" after a success). Order is
  // preserved so the parent can focus the most recently created team.
  const [sessionCreatedTeamIds, setSessionCreatedTeamIds] = useState<string[]>(
    []
  );
  // Post-import merge CTA. When the user chose "import as new" for one or more
  // flagged duplicates, we resolve the newly-created prospect + its existing
  // match and seed the existing MergeProspectsModal. `mergePairs` are the
  // resolved [new, existing] prospect pairs queued for the merge CTA;
  // `mergeMessage` is a graceful fallback shown when resolution failed.
  const [mergePairs, setMergePairs] = useState<[ProspectResponse, ProspectResponse][]>([]);
  const [activeMergePairIndex, setActiveMergePairIndex] = useState<number | null>(null);
  // Indices of `mergePairs` the user actually merged (via the modal's onSuccess).
  // Canceling a pair does not add it here, so the CTA count reflects only
  // duplicates that are still unresolved.
  const [mergedPairIndices, setMergedPairIndices] = useState<Set<number>>(new Set());
  const [mergeMessage, setMergeMessage] = useState<string>("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  // Generation token for the fire-and-forget post-import merge-pair resolution.
  // Bumped whenever the modal closes/resets or the user starts another upload, so a
  // late roster fetch can't resurrect a merge queue for an abandoned import.
  const resolveRunRef = useRef(0);

  // Which players on the uploaded roster carried a mailing address. Derived from the mapped rows
  // the modal already holds, so the success step can answer "who got an address and who did not"
  // without a second round trip. Null whenever the roster had no address column at all.
  const addressSummary = useMemo(
    () => summarizeRosterAddresses(mappedData),
    [mappedData]
  );

  // A team that failed to import saved nothing, so the readout must not claim every address
  // reached a record. Only the multi-team path can partially fail.
  const addressSummaryLines = useMemo(() => {
    if (!addressSummary) return [];
    const allImported =
      successInfo?.type !== "multi_team" || successInfo.failedTeams.length === 0;
    return describeRosterAddresses(addressSummary, { allImported });
  }, [addressSummary, successInfo]);

  const unresolvedMergeCount = mergePairs.length - mergedPairIndices.size;
  const firstUnresolvedMergeIndex = mergePairs.findIndex(
    (_, i) => !mergedPairIndices.has(i)
  );

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setCurrentStep("type-selection");
      setUploadType("single_team");
      setSelectedFile(null);
      setSourceType("image");
      setGcsUrl("");
      setRosterUploadId("");
      setExtractionId("");
      setExtractedData(null);
      setMappedData(null);
      setSuccessInfo(null);
      setError("");
      setIsUploading(false);
      setIsDragging(false);
      setHasSuccessfulUpload(false);
      setExtractionAttempt(0);
      setSessionCreatedTeamIds([]);
      setMergePairs([]);
      setActiveMergePairIndex(null);
      setMergedPairIndices(new Set());
      setMergeMessage("");
      resolveRunRef.current++;
    }
  }, [isOpen]);

  // Keep focus inside the modal when it opens and on every step change, since the control that
  // advanced the step has just unmounted and focus would otherwise fall to <body>. A step's tour
  // remembers what had focus when it starts, so without this, skipping it sent focus to the page
  // heading behind the modal. A nested dialog (the cancel confirmation, a merge) keeps focus while
  // it is open, even when the step changes underneath it, as it does when extraction finishes.
  const nestedDialogOpen = showCancelConfirm || activeMergePairIndex !== null;
  useEffect(() => {
    const modal = modalRef.current;
    if (!isOpen || !modal || nestedDialogOpen || modal.contains(document.activeElement)) return;
    modal.focus({ preventScroll: true });
  }, [isOpen, currentStep, nestedDialogOpen]);

  const validateFile = (file: File): string | null => {
    if (file.size > MAX_FILE_SIZE) {
      return `File size must be less than 50MB. Your file is ${(
        file.size /
        1024 /
        1024
      ).toFixed(1)}MB.`;
    }

    if (
      !ALLOWED_FILE_TYPES.includes(file.type) &&
      !file.name.match(/\.(jpg|jpeg|png|pdf|csv|xlsx|xls)$/i)
    ) {
      return "Invalid file type. Please upload JPG, PNG, PDF, CSV, or Excel files.";
    }

    return null;
  };

  const handleFileSelect = (file: File) => {
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    setSelectedFile(file);
    setSourceType(getSourceType(file));
    setError("");
  };

  const handleFileInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragging(false);

    const file = event.dataTransfer.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleTypeSelectionContinue = () => {
    setCurrentStep("upload");
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    const traceId = SentryService.generateTraceId();
    const startTime = Date.now();

    SentryService.setUploadContext({
      trace_id: traceId,
      source_type: sourceType,
      file_name: selectedFile.name,
      file_size: selectedFile.size,
      operation: 'upload',
      phase: 'gcs_upload',
      start_time: new Date(startTime).toISOString(),
    });

    SentryService.addUploadBreadcrumb('Starting roster file upload', {
      trace_id: traceId,
      file_name: selectedFile.name,
      file_size: selectedFile.size,
      source_type: sourceType,
    });

    setIsUploading(true);
    setError("");

    try {
      SentryService.addUploadBreadcrumb('Uploading file to GCS');
      const { gcsUrl: uploadedGcsUrl, imageId: uploadedImageId } =
        await uploadRosterFile(selectedFile);

      SentryService.setUploadContext({
        trace_id: traceId,
        source_type: sourceType,
        file_name: selectedFile.name,
        file_size: selectedFile.size,
        gcs_url: uploadedGcsUrl,
        operation: 'upload',
        phase: 'gcs_upload_complete',
        start_time: new Date(startTime).toISOString(),
        duration_ms: Date.now() - startTime,
      });

      setGcsUrl(uploadedGcsUrl);
      setRosterUploadId(uploadedImageId);
      setSelectedFile(null);
      setCurrentStep("extracting");
    } catch (err) {
      const durationMs = Date.now() - startTime;

      SentryService.captureError('[UPLOAD FAILED] File upload to GCS failed', {
        operation: 'upload',
        phase: 'gcs_upload',
        tags: {
          error_category: 'extraction_failed',
          upload_phase: 'file_upload',
          screen: 'RosterUploadModal',
        },
        extra: {
          error: err instanceof Error ? err.message : String(err),
          file_name: selectedFile.name,
          file_size: selectedFile.size,
          source_type: sourceType,
          duration_ms: durationMs,
        },
        fingerprint: ['roster-upload', 'gcs-upload', 'failed'],
      });

      setError(getSafeErrorMessage(err));
    } finally {
      setIsUploading(false);
    }
  };

  const handleExtractionComplete = (data: ParsedCSV, completedExtractionId: string) => {
    setExtractedData(data);
    setExtractionId(completedExtractionId);
    setCurrentStep("mapping");
  };

  const handleExtractionError = (errorMessage: string) => {
    setError(errorMessage);
  };

  const handleExtractionRetry = () => {
    setCurrentStep("upload");
    setGcsUrl("");
    setRosterUploadId("");
    setExtractionId("");
    setError("");
  };

  const handleReExtract = () => {
    setExtractedData(null);
    setExtractionId("");
    setMappedData(null);
    setError("");
    setExtractionAttempt((prev) => prev + 1);
    setCurrentStep("extracting");
  };

  const handleMappingComplete = (data: ParsedCSV) => {
    setMappedData(data);
    if (
      uploadType === "multi_team" ||
      uploadType === "showcase" ||
      data.rosterType === "showcase"
    ) {
      setCurrentStep("multi-team-review");
    } else {
      setCurrentStep("team-selection");
    }
  };

  const handleMappingBack = () => {
    setCurrentStep("extracting");
  };

  const handleTeamSelectionComplete = (
    teamId: string,
    teamName: string,
    prospectCount: number,
    importAsNewCandidates?: ImportAsNewCandidate[]
  ) => {
    setSuccessInfo({ type: "team", teamId, teamName, prospectCount });
    setCurrentStep("success");
    setHasSuccessfulUpload(true);
    if (teamId) {
      setSessionCreatedTeamIds((prev) => [...prev, teamId]);
    }

    // Route "import as new" duplicates into the existing merge workflow. The
    // records now exist, so we resolve the freshly-created prospect for each
    // candidate and pair it with its existing match to seed MergeProspectsModal.
    if (importAsNewCandidates && importAsNewCandidates.length > 0 && teamId) {
      void resolveMergePairs(teamId, importAsNewCandidates);
    }
  };

  /**
   * Resolve the newly-created prospects for the "import as new" candidates and
   * build [new, existing] pairs to seed the merge modal. Matching is by
   * firstName + lastName (case-insensitive) plus gradYear/position when present,
   * excluding the existing match's id so we pick the NEW record. Degrades to a
   * visible message (never crashes the success flow) when resolution is
   * ambiguous or fails.
   *
   * NOTE: full field-level merge is intentionally deferred; this only opens the
   * existing manual merge modal seeded with the two records.
   */
  const resolveMergePairs = async (
    teamId: string,
    candidates: ImportAsNewCandidate[]
  ) => {
    const runId = ++resolveRunRef.current;
    try {
      const teamProspects = await getAllProspectsForTeam(teamId);
      // If the modal was closed/reset or another upload started while this fetch was
      // in flight, this run is stale — do not resurrect a merge queue for it.
      if (runId !== resolveRunRef.current) return;
      const pairs: [ProspectResponse, ProspectResponse][] = [];

      for (const candidate of candidates) {
        const existing = teamProspects.find((p) => p.id === candidate.existingMatchProspectId);

        // Candidate records that match on name (and grad year / position when the
        // candidate carried them), excluding the existing match itself.
        const matches = teamProspects.filter((p) => {
          if (p.id === candidate.existingMatchProspectId) return false;
          const nameMatch =
            p.firstName.trim().toLowerCase() === candidate.firstName.trim().toLowerCase() &&
            p.lastName.trim().toLowerCase() === candidate.lastName.trim().toLowerCase();
          if (!nameMatch) return false;
          if (candidate.gradYear != null && p.gradYear !== candidate.gradYear) return false;
          if (candidate.position && p.position !== candidate.position) return false;
          return true;
        });

        // Only seed a pair when resolution is unambiguous: exactly one new record
        // and the existing match is still present. Anything else falls through to
        // the graceful message below.
        if (existing && matches.length === 1) {
          pairs.push([matches[0], existing]);
        }
      }

      if (pairs.length > 0) {
        setMergePairs(pairs);
        setActiveMergePairIndex(0);
      }
      if (pairs.length < candidates.length) {
        // Some candidates could not be resolved cleanly — point the user at the
        // roster's Merge tool for those rather than guessing.
        setMergeMessage(
          "Some imported players may duplicate existing records. Use the Merge tool on the roster to review and combine them."
        );
      }
    } catch (err) {
      // Never let the merge CTA break a successful import.
      SentryService.addUploadBreadcrumb("Failed to resolve merge pairs post-import", {
        error: err instanceof Error ? err.message : String(err),
      });
      if (runId !== resolveRunRef.current) return;
      setMergeMessage(
        "We couldn't automatically match the imported players. Use the Merge tool on the roster to review possible duplicates."
      );
    }
  };

  /** Advance to the next queued merge pair, or close the CTA when done. */
  /** Advance to the next not-yet-merged pair (using the given resolved set), or close. */
  const advanceMergePairFrom = (resolved: Set<number>) => {
    setActiveMergePairIndex((prev) => {
      if (prev === null) return null;
      for (let next = prev + 1; next < mergePairs.length; next++) {
        if (!resolved.has(next)) return next;
      }
      return null;
    });
  };

  /**
   * A merge completed for the active pair. Record it as resolved (so the CTA
   * count decrements and does not keep advertising an already-merged duplicate),
   * then advance to the next queued pair — using the freshly-updated resolved set
   * so we never re-open an already-merged pair.
   */
  const handleMergePairSuccess = () => {
    const merged =
      activeMergePairIndex === null
        ? mergedPairIndices
        : new Set(mergedPairIndices).add(activeMergePairIndex);
    setMergedPairIndices(merged);
    advanceMergePairFrom(merged);
  };

  /** The user dismissed the merge modal without merging — leave the pair unresolved. */
  const handleMergePairCancel = () => {
    advanceMergePairFrom(mergedPairIndices);
  };

  const handleMultiTeamComplete = (
    teamCount: number,
    prospectCount: number,
    skippedCount: number,
    duplicateCount: number,
    failedTeams: { teamName: string; error: string }[],
    createdTeamIds: string[]
  ) => {
    setSuccessInfo({
      type: "multi_team",
      teamCount,
      prospectCount,
      skippedCount,
      duplicateCount,
      failedTeams,
      createdTeamIds,
    });
    setCurrentStep("success");
    setHasSuccessfulUpload(true);
    if (createdTeamIds.length > 0) {
      setSessionCreatedTeamIds((prev) => [...prev, ...createdTeamIds]);
    }
  };

  const handleTeamSelectionBack = () => {
    setCurrentStep("mapping");
  };

  const handleMultiTeamBack = () => {
    setCurrentStep("mapping");
  };

  const handleAddMore = () => {
    setCurrentStep("type-selection");
    setUploadType("single_team");
    setSelectedFile(null);
    setSourceType("image");
    setGcsUrl("");
    setRosterUploadId("");
    setExtractionId("");
    setExtractedData(null);
    setMappedData(null);
    setSuccessInfo(null);
    setError("");
    setMergePairs([]);
    setActiveMergePairIndex(null);
    setMergedPairIndices(new Set());
    setMergeMessage("");
    resolveRunRef.current++;
  };

  const handleDone = () => {
    onSuccess(sessionCreatedTeamIds);
    onClose();
  };

  const handleCloseModal = () => {
    if (
      currentStep === "extracting" ||
      currentStep === "team-selection" ||
      currentStep === "multi-team-review"
    ) {
      setShowCancelConfirm(true);
    } else {
      if (hasSuccessfulUpload) {
        onSuccess(sessionCreatedTeamIds);
      }
      onClose();
    }
  };

  const handleConfirmClose = () => {
    setShowCancelConfirm(false);
    onClose();
  };

  const handleCancelClose = () => {
    setShowCancelConfirm(false);
  };

  if (!isOpen) return null;

  // Determine progress step states
  const isAfterType = currentStep !== "type-selection";
  const isAfterUpload = currentStep !== "type-selection" && currentStep !== "upload" && currentStep !== "extracting";
  const isAfterMapping = currentStep === "team-selection" || currentStep === "multi-team-review" || currentStep === "success";
  const isComplete = currentStep === "success";

  return (
    <div className="roster-upload-modal-overlay" onClick={handleCloseModal}>
      <div
        ref={modalRef}
        className="roster-upload-modal"
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2>Upload Roster</h2>
          <div className="roster-upload-modal__header-actions">
            <ModalCloseButton onClick={handleCloseModal} label="upload modal" />
          </div>
        </div>

        <div className="modal-progress">
          <div
            className={`progress-step ${
              currentStep === "type-selection" ? "active" : ""
            } ${isAfterType ? "completed" : ""}`}
          >
            1. Type
          </div>
          <div className="progress-line"></div>
          <div
            className={`progress-step ${
              currentStep === "upload" || currentStep === "extracting"
                ? "active"
                : ""
            } ${isAfterUpload ? "completed" : ""}`}
          >
            2. Upload
          </div>
          <div className="progress-line"></div>
          <div
            className={`progress-step ${
              currentStep === "mapping" ? "active" : ""
            } ${isAfterMapping ? "completed" : ""}`}
          >
            3. Map Fields
          </div>
          <div className="progress-line"></div>
          <div
            className={`progress-step ${
              currentStep === "team-selection" ||
              currentStep === "multi-team-review"
                ? "active"
                : ""
            } ${isComplete ? "completed" : ""}`}
          >
            4. {uploadType === "multi_team" ? "Review" : "Select Team"}
          </div>
          <div className="progress-line"></div>
          <div
            className={`progress-step ${
              isComplete ? "active completed" : ""
            }`}
          >
            5. Complete
          </div>
        </div>

        <div className="modal-content">
          {currentStep === "type-selection" && (
            <UploadTypeStep
              selected={uploadType}
              onSelect={setUploadType}
              isJucoUpload={isJucoUpload}
              onJucoUploadChange={setIsJucoUpload}
              onContinue={handleTypeSelectionContinue}
            />
          )}

          {currentStep === "upload" && (
            <div className="upload-step">
              <div
                className={`file-drop-zone ${isDragging ? "dragging" : ""} ${
                  selectedFile ? "has-file" : ""
                }`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                {...activate(() => fileInputRef.current?.click())}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".jpg,.jpeg,.png,.pdf,.csv,.xlsx,.xls"
                  onChange={handleFileInputChange}
                  style={{ display: "none" }}
                />

                {selectedFile ? (
                  <>
                    <div className="file-icon">📄</div>
                    <div className="file-name">{selectedFile.name}</div>
                    <div className="file-size">
                      {selectedFile.size > 0
                        ? `${(selectedFile.size / 1024 / 1024).toFixed(2)} MB`
                        : "0 MB"}
                    </div>
                    <button
                      className="change-file-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        fileInputRef.current?.click();
                      }}
                    >
                      Change File
                    </button>
                  </>
                ) : (
                  <>
                    <div className="upload-icon">☁️</div>
                    <p className="drop-text">
                      Drag and drop your roster file here
                    </p>
                    <p className="or-text">or</p>
                    <button className="browse-button">Browse Files</button>
                    <p className="file-types">
                      Supported: JPG, PNG, PDF, CSV, Excel (max 50MB)
                    </p>
                  </>
                )}
              </div>

              {error && <div className="error-message">{error}</div>}

              {selectedFile && !error && (
                <div className="upload-actions">
                  <button
                    className="upload-button"
                    onClick={handleUpload}
                    disabled={isUploading}
                  >
                    {isUploading ? "Uploading..." : "Upload & Extract"}
                  </button>
                </div>
              )}
            </div>
          )}

          {currentStep === "extracting" && (
            <ExtractionProgress
              key={extractionAttempt}
              gcsUrl={gcsUrl}
              sourceType={sourceType}
              uploadType={uploadType}
              onComplete={handleExtractionComplete}
              onError={handleExtractionError}
              onRetry={handleExtractionRetry}
            />
          )}

          {currentStep === "mapping" && extractedData && (
            <FieldMappingStep
              extractedData={extractedData}
              onComplete={handleMappingComplete}
              onBack={handleMappingBack}
              onRetry={handleReExtract}
            />
          )}

          {currentStep === "team-selection" && mappedData && (
            <TeamSelectionStep
              mappedData={mappedData}
              defaultIsJucoRoster={isJucoUpload}
              rosterUploadId={
                sourceType === "image" ? rosterUploadId : undefined
              }
              extractionId={extractionId || undefined}
              onComplete={handleTeamSelectionComplete}
              onBack={handleTeamSelectionBack}
            />
          )}

          {currentStep === "multi-team-review" && mappedData && (
            <MultiTeamReviewStep
              mappedData={mappedData}
              defaultIsJucoRoster={isJucoUpload}
              rosterUploadId={
                sourceType === "image" ? rosterUploadId : undefined
              }
              extractionId={extractionId || undefined}
              onComplete={handleMultiTeamComplete}
              onBack={handleMultiTeamBack}
            />
          )}

          {currentStep === "success" && successInfo && (
            <div className="success-step">
              <div className="success-icon">✓</div>
              <h3>Success!</h3>
              <p>
                {successInfo.type === "team" ? (
                  <>
                    Successfully added {successInfo.prospectCount} prospect
                    {successInfo.prospectCount !== 1 ? "s" : ""} to{" "}
                    {successInfo.teamName}
                  </>
                ) : (
                  <>
                    Successfully imported {successInfo.prospectCount} prospect
                    {successInfo.prospectCount !== 1 ? "s" : ""} across{" "}
                    {successInfo.teamCount} team
                    {successInfo.teamCount !== 1 ? "s" : ""}
                  </>
                )}
              </p>

              {successInfo.type === "multi_team" && successInfo.skippedCount > 0 && (
                <div className="success-warning">
                  {successInfo.skippedCount} player{successInfo.skippedCount !== 1 ? "s were" : " was"} skipped (missing name)
                </div>
              )}

              {successInfo.type === "multi_team" && successInfo.duplicateCount > 0 && (
                <div className="success-warning">
                  {successInfo.duplicateCount} duplicate{successInfo.duplicateCount !== 1 ? "s were" : " was"} skipped (same player on same team)
                </div>
              )}

              {successInfo.type === "multi_team" && successInfo.failedTeams.length > 0 && (
                <div className="success-errors">
                  <strong>
                    {successInfo.failedTeams.length} team{successInfo.failedTeams.length !== 1 ? "s" : ""} failed to import:
                  </strong>
                  <ul>
                    {successInfo.failedTeams.map((ft, idx) => (
                      <li key={idx}>{ft.teamName}: {ft.error}</li>
                    ))}
                  </ul>
                </div>
              )}

              {addressSummaryLines.length > 0 && (
                <div className="success-addresses" data-testid="address-summary">
                  {addressSummaryLines.map((line, index) => (
                    <p
                      key={line}
                      className={index > 0 ? "success-addresses__missing" : undefined}
                    >
                      {line}
                    </p>
                  ))}
                </div>
              )}

              {unresolvedMergeCount > 0 && (
                <div className="success-merge-cta" data-testid="merge-cta">
                  <p>
                    {unresolvedMergeCount} imported player
                    {unresolvedMergeCount !== 1 ? "s" : ""} may duplicate an existing record.
                  </p>
                  <Button
                    variant="secondary"
                    onClick={() => setActiveMergePairIndex(firstUnresolvedMergeIndex)}
                    data-testid="review-merge-button"
                  >
                    Review possible duplicates
                  </Button>
                </div>
              )}

              {mergePairs.length > 0 && unresolvedMergeCount === 0 && (
                <div className="success-merge-cta" data-testid="merge-resolved">
                  <p>
                    Merged {mergedPairIndices.size} possible duplicate
                    {mergedPairIndices.size !== 1 ? "s" : ""}.
                  </p>
                </div>
              )}

              {mergeMessage && (
                <div className="success-warning" data-testid="merge-message">
                  {mergeMessage}
                </div>
              )}

              <div className="success-actions">
                <button className="secondary-button" onClick={handleAddMore}>
                  Add More
                </button>
                <button className="primary-button" onClick={handleDone}>
                  Done
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <ConfirmModal
        isOpen={showCancelConfirm}
        onClose={handleCancelClose}
        onConfirm={handleConfirmClose}
        title="Cancel Upload?"
        message="Upload is in progress. Canceling now will lose your progress. Are you sure you want to cancel?"
        confirmText="Yes, Cancel"
        cancelText="No, Continue"
        icon={<span style={{ fontSize: "48px" }}>⚠️</span>}
      />

      {/*
        Reuse the existing manual merge workflow for "import as new" duplicates.
        Seeded with the [new, existing] pair; advancing (or canceling) moves to
        the next queued pair. Full field-level merge lives in the modal itself.
      */}
      {activeMergePairIndex !== null && mergePairs[activeMergePairIndex] && (
        <MergeProspectsModal
          // Remount per pair so the modal's isOpen-keyed reset effect runs and
          // no survivor/field state leaks from the previously resolved pair.
          key={activeMergePairIndex}
          isOpen={true}
          prospects={mergePairs[activeMergePairIndex]}
          onClose={handleMergePairCancel}
          // The merge itself is committed server-side by the modal. We intentionally
          // don't consume its (survivorId, mergedIds, result) payload here: this modal
          // isn't rendering a prospect list to evict from — closing returns to the
          // dashboard, which refetches. We only track that the pair was resolved.
          onSuccess={handleMergePairSuccess}
        />
      )}
    </div>
  );
}
